import { t, sprache } from './i18n.js';

/** Anzeigehilfen, Zahlen, Zeiten und Beträge, überall gleich formatiert. */

function locale(): string {
  return sprache() === 'en' ? 'en-US' : 'de-DE';
}

function nf(): Intl.NumberFormat {
  return new Intl.NumberFormat(locale());
}

function cf(): Intl.NumberFormat {
  return new Intl.NumberFormat(locale(), { style: 'currency', currency: 'EUR' });
}

export function num(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return nf().format(value);
}

/** Große Zahlen kurz: 41.200 → 41,2 k */
export function compact(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) < 1000) return nf().format(value);
  if (Math.abs(value) < 1_000_000) return `${nf().format(Number((value / 1000).toFixed(1)))} k`;
  return `${nf().format(Number((value / 1_000_000).toFixed(1)))} M`;
}

export function eur(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return cf().format(value);
}

export function eurPrecise(value: number): string {
  return new Intl.NumberFormat(locale(), {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: value < 0.1 ? 4 : 2,
    maximumFractionDigits: value < 0.1 ? 4 : 2,
  }).format(value);
}

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** „heute 18:00", „morgen 09:00", „Sa 11:00", „14.09. 18:00" */
export function when(iso: string | Date | null | undefined): string {
  if (!iso) return t('ohne Termin');
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return t('ohne Termin');

  const diff = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86_400_000);
  const time = d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });

  if (diff === 0) return t('heute {time}', { time });
  if (diff === 1) return t('morgen {time}', { time });
  if (diff === -1) return t('gestern {time}', { time });
  if (diff > 1 && diff < 7) return `${d.toLocaleDateString(locale(), { weekday: 'short' })} ${time}`;
  return `${d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit' })} ${time}`;
}

/** „vor 3 Min.", „vor 2 Std." */
export function ago(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return t('gerade eben');
  if (seconds < 3600) return t('vor {n} Min.', { n: Math.floor(seconds / 60) });
  if (seconds < 86_400) return t('vor {n} Std.', { n: Math.floor(seconds / 3600) });
  if (seconds < 604_800) return t('vor {n} Tg.', { n: Math.floor(seconds / 86_400) });
  return d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * Datum und Uhrzeit ausgeschrieben, fuer den Mauszeiger-Hinweis.
 *
 * Die Kurzform („vor 3 Std.") beantwortet „wie frisch ist das", nicht
 * „wann genau war das". Beides an derselben Stelle zu zeigen macht die
 * Zeile unruhig, also steht die genaue Angabe im title-Attribut.
 */
export function exact(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale(), {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function bytes(value: number | null | undefined): string {
  if (!value) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = value;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export function duration(seconds: number | null | undefined): string {
  // 0 ist ein gültiger Wert, im Player steht dort der Anfang des Tracks,
  // nicht „keine Angabe". Nur null und undefined sind wirklich leer.
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* ------------------------------------------------------ Status-Vokabular */

/*
 * Ein Status, eine Farbe, überall dieselbe (README §6.12):
 *   warn  = du bist dran (Entwurf wartet auf Freigabe)
 *   accent = geplant, der Agent arbeitet (baut, rendert, postet)
 *   ok    = veröffentlicht
 *   err   = fehlgeschlagen oder nur teilweise veröffentlicht
 */
export const STATUS_LABEL: Record<string, string> = {
  idea: 'Idee',
  generating: 'Wird gebaut',
  draft: 'Wartet auf dich',
  approved: 'Geplant',
  rendering: 'Rendert',
  scheduled: 'Geplant',
  publishing: 'Wird gepostet',
  published: 'Veröffentlicht',
  platform_draft: 'In TikTok fertigstellen',
  failed: 'Fehlgeschlagen',
  archived: 'Verworfen',
};

/** Kurzwort für enge Stellen wie die Kalenderkarte. */
export const STATUS_SHORT: Record<string, string> = {
  idea: 'Idee',
  generating: 'Baut',
  draft: 'Prüfen',
  approved: 'Geplant',
  rendering: 'Rendert',
  scheduled: 'Geplant',
  publishing: 'Postet',
  published: 'Live',
  platform_draft: 'In TikTok',
  failed: 'Fehler',
  archived: 'Verworfen',
};

export type StatusTone = 'warn' | 'accent' | 'ok' | 'err' | 'idle';

type PublishResultState = { state: string };

function hasPartialSuccess(status: string, results?: readonly PublishResultState[]): boolean {
  return status === 'failed' && !!results?.some((result) => result.state === 'ok' || result.state === 'draft_on_platform');
}

export function isPartial(status: string, results?: readonly PublishResultState[]): boolean {
  return hasPartialSuccess(status, results);
}

/** Beschriftung eines Status, übersetzt. Über diese Funktion, nicht das Wörterbuch direkt. */
export function statusLabel(status: string, results?: readonly PublishResultState[]): string {
  if (hasPartialSuccess(status, results)) return t('Teilweise veröffentlicht');
  return t(STATUS_LABEL[status] ?? status);
}

export function statusShort(status: string, results?: readonly PublishResultState[]): string {
  if (hasPartialSuccess(status, results)) return t('Teilweise');
  return t(STATUS_SHORT[status] ?? status);
}

export function statusTone(status: string, results?: readonly PublishResultState[]): StatusTone {
  if (hasPartialSuccess(status, results)) return 'err';
  switch (status) {
    case 'draft': return 'warn';
    case 'approved':
    case 'scheduled':
    case 'rendering':
    case 'generating':
    case 'publishing': return 'accent';
    case 'published': return 'ok';
    // Noch nicht draußen: Der Entwurf wartet in der TikTok-App auf den Nutzer.
    case 'platform_draft': return 'warn';
    case 'failed': return 'err';
    default: return 'idle';
  }
}

/** Die CSS-Farbe eines Tons, für Ränder und Punkte. */
export function toneColor(tone: StatusTone): string {
  return tone === 'idle' ? 'var(--text3)' : tone === 'accent' ? 'var(--accent)' : `var(--${tone})`;
}

export function statusPill(status: string, results?: readonly PublishResultState[]): string {
  const tone = statusTone(status, results);
  return tone === 'idle' ? 'pill' : tone === 'accent' ? 'pill pill-info' : `pill pill-${tone}`;
}

/** Läuft für diesen Status gerade etwas? (Spinner in der Pille) */
export function statusBusy(status: string): boolean {
  return status === 'generating' || status === 'rendering' || status === 'publishing';
}

/*
 * Seit es den Foto-Post gibt, sagt „TikTok" allein nicht mehr, was daraus
 * wird: „Video" ist ein Clip, „Fotos" eine Bildserie zum Durchwischen.
 * Deshalb steht die Gestalt in der Beschriftung, wie bei Instagram auch.
 */
export const TARGET_LABEL: Record<string, string> = {
  tiktok: 'TikTok Video',
  tiktok_photo: 'TikTok Fotos',
  ig_reels: 'IG Reels',
  ig_feed: 'IG Feed',
  ig_story: 'IG Story',
  fb_reels: 'Facebook Reel',
  fb_feed: 'Facebook Beitrag',
};

export function targetsLabel(targets: string[] | undefined): string {
  if (!targets?.length) return '—';
  // „tiktok_video" war ein falscher Schlüssel älterer Posts, gemeint ist das TikTok-Video.
  return targets.map((ziel) => t(TARGET_LABEL[ziel === 'tiktok_video' ? 'tiktok' : ziel] ?? ziel)).join(' · ');
}

/** Wandelt einen Akzent-Hex in eine leicht getönte Fläche. */
export function tint(hex: string, percent: number): string {
  return `color-mix(in oklab, ${hex} ${percent}%, transparent)`;
}
